import { useEffect, useState, Fragment } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import ErrorState from '@/components/ErrorState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, CheckCircle2, Filter, MessageCircle, ArrowUpDown, Star } from 'lucide-react';
import { ReportButton } from '@/components/ReportButton';
import { formatPrice, convert, toCode, symbolOf, CURRENCIES } from '@/data/money';
import { questionGrade } from '@/data/scoring';
import { reqNo } from '@/data/format';
import AccuracyMeter from '@/components/AccuracyMeter';
import { toast } from 'sonner';

const CompareAnswers = () => {
  const { requestId } = useParams();
  const { config, rates, displayCurrency, setDisplayCurrency } = useAuth();
  const free = config?.free_mode;
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [filteredAnswers, setFilteredAnswers] = useState([]);
  const [priceFilter, setPriceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [myOrders, setMyOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [truthInputs, setTruthInputs] = useState({});
  const [savingTruths, setSavingTruths] = useState(false);

  useEffect(() => {
    fetchData();
    const timer = setInterval(() => fetchData(), 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const fetchData = async () => {
    try {
      const [reqRes, ansRes, ordersRes] = await Promise.all([
        axios.get(`${API}/requests/${requestId}`, { withCredentials: true }),
        axios.get(`${API}/requests/${requestId}/answers`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setRequest(reqRes.data);
      setAnswers(ansRes.data);
      setFilteredAnswers(ansRes.data);
      setMyOrders((ordersRes.data || []).filter(o => o.request_id === requestId));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('無法載入資料');
    } finally {
      setLoading(false);
    }
  };

  // Backfill the querent's own known answers (truths) -> backend scores every answer.
  const submitTruths = async () => {
    const qs = request.verification_questions || [];
    const myTruths = request.my_truths || {};
    const truths = {};
    for (const q of qs) {
      if (myTruths[String(q.order)]) continue;   // already locked
      const t = (truthInputs[q.order] || '').trim();
      if (!t) { toast.error('真相要一次過填曬所有問題'); return; }
      truths[q.order] = t;
    }
    if (!Object.keys(truths).length) return;
    setSavingTruths(true);
    try {
      await axios.put(`${API}/requests/${requestId}/verification-truths`, { truths }, { withCredentials: true });
      setTruthInputs({});
      await fetchData();
      toast.success('真相已儲存，評分完成後會更新');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '提交失敗');
    } finally {
      setSavingTruths(false);
    }
  };

  useEffect(() => {
    applyFilterAndSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceFilter, sortBy, answers, rates, displayCurrency]);

  // Normalise each quote to TWD so filter buckets + price sort compare like-for-like
  // across astrologers who quote in different currencies.
  const inTWD = (a) => convert(a.price, toCode(a.currency), 'TWD', rates);

  const applyFilterAndSort = () => {
    let result = answers;
    if (priceFilter === 'low') {
      result = result.filter(a => inTWD(a) <= 2000);
    } else if (priceFilter === 'medium') {
      result = result.filter(a => inTWD(a) > 2000 && inTWD(a) <= 3500);
    } else if (priceFilter === 'high') {
      result = result.filter(a => inTWD(a) > 3500);
    }

    if (sortBy !== 'default') {
      result = [...result];
      if (sortBy === 'price-asc') {
        result.sort((a, b) => inTWD(a) - inTWD(b));
      } else if (sortBy === 'price-desc') {
        result.sort((a, b) => inTWD(b) - inTWD(a));
      } else if (sortBy === 'time-asc') {
        result.sort((a, b) => a.consultation_time_minutes - b.consultation_time_minutes);
      } else if (sortBy === 'time-desc') {
        result.sort((a, b) => b.consultation_time_minutes - a.consultation_time_minutes);
      } else if (sortBy === 'score-desc') {
        // Unscored answers (null) sink below any scored one.
        result.sort((a, b) => (b.answer_score ?? -1) - (a.answer_score ?? -1));
      }
    }

    // Starred answers always float to the top (stable over the chosen sort).
    result = [...result].sort((a, b) => (b.starred ? 1 : 0) - (a.starred ? 1 : 0));
    setFilteredAnswers(result);
  };

  const handleSelectAstrologer = (astrologerId) => {
    navigate(`/client/payment/${requestId}/${astrologerId}`);
  };

  const orderFor = (astrologerId) => myOrders.find((o) => o.astrologer_id === astrologerId);
  const maxPicks = config?.max_picks_per_request || 1;
  const isFull = myOrders.length >= maxPicks;
  const requestClosed = !!request && (request.status !== 'open' || request.expired);

  const toggleStar = async (answer) => {
    const next = !answer.starred;
    setAnswers((prev) => prev.map((a) => (a.answer_id === answer.answer_id ? { ...a, starred: next } : a)));
    try {
      await axios.post(`${API}/requests/${requestId}/star`, { astrologer_id: answer.astrologer_id, starred: next }, { withCredentials: true });
    } catch (e) {
      setAnswers((prev) => prev.map((a) => (a.answer_id === answer.answer_id ? { ...a, starred: !next } : a)));
      toast.error('操作失敗');
    }
  };

  const StarButton = ({ answer }) => (
    <button
      type="button"
      onClick={() => toggleStar(answer)}
      className="flex-shrink-0 p-1 -m-1 rounded-sm hover:bg-[#F5F0E6]"
      data-testid={`star-${answer.answer_id}`}
      aria-label={answer.starred ? '取消星標' : '星標'}
    >
      <Star className={`w-5 h-5 ${answer.starred ? 'fill-[#E8A33D] text-[#E8A33D]' : 'text-[#C9C2B5]'}`} strokeWidth={1.5} />
    </button>
  );

  const Quotes = ({ answer, className = '' }) => {
    const cur = answer.currency;
    const list = answer.quotes && answer.quotes.length
      ? answer.quotes
      : [{ scope: '諮詢', price: answer.price, consultation_time_minutes: answer.consultation_time_minutes }];
    return (
      <div className={`text-sm space-y-0.5 ${className}`}>
        {list.map((q) => {
          const f = formatPrice(q.price, cur, displayCurrency, rates);
          return (
            <div key={q.scope} className="flex justify-between gap-3">
              <span className="text-[#5C5C5C]">{q.scope}</span>
              <span className="whitespace-nowrap text-right">
                <span className="text-[#C93B2B] font-medium">{f.text}</span>
                <span className="text-[#5C5C5C]"> / {q.consultation_time_minutes}分</span>
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Shared bits ----------------------------------------------------------------
  const Badges = ({ profile }) =>
    profile ? (
      <div className="flex flex-wrap gap-1 mb-2">
        {profile.specialties.slice(0, 2).map((s) => (
          <span key={`spec-${s}`} className="inline-flex items-center border border-[#C93B2B] text-[#C93B2B] px-2 py-0.5 text-[11px] rounded-none font-serif tracking-widest">
            {s}
          </span>
        ))}
      </div>
    ) : null;


  const SelectButton = ({ answer, className = '' }) => {
    const ord = orderFor(answer.astrologer_id);
    if (ord) {
      return (
        <Link to={`/chat/${ord.order_id}`} className={`block ${className}`} data-testid={`go-chat-${answer.answer_id}`}>
          <Button
            variant="outline"
            className="w-full rounded-sm border-[#C93B2B] text-[#C93B2B] hover:bg-[#F5F0E6]"
          >
            <MessageCircle className="mr-2 w-4 h-4" strokeWidth={1.5} />
            前往諮詢室
          </Button>
        </Link>
      );
    }
    return (
      <Button
        onClick={() => handleSelectAstrologer(answer.astrologer_id)}
        disabled={isFull || requestClosed}
        className={`bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shadow-sm disabled:bg-[#E5E0D8] disabled:text-[#5C5C5C] disabled:cursor-not-allowed ${className}`}
        data-testid={`select-astrologer-${answer.answer_id}`}
      >
        <CheckCircle2 className="mr-2 w-4 h-4" strokeWidth={1.5} />
        {requestClosed ? '求測單已完成' : isFull ? `已選滿 ${maxPicks} 位` : '選擇此命理師'}
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">載入中...</p>
      </div>
    );
  }

  if (!request || answers.length === 0) {
    return <ErrorState message="無法比較答案" />;
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4">
        <Link to="/client/dashboard">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台</h2>
        </Link>
      </nav>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <Link to={`/client/request/${requestId}`}>
          <Button variant="ghost" className="mb-6 rounded-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            返回
          </Button>
        </Link>

        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#1A1A1A] mb-2">
            比較命理師回答
          </h1>
          {request?.seq && <p className="text-xs text-[#9C9C9C] font-mono mb-2">{reqNo(request.seq)}</p>}
          <p className="text-base text-[#5C5C5C] mb-8">
            並排比較每位命理師對同一條問題的回答，選擇最適合的一位
          </p>

          {myOrders.length > 0 && (
            <div className="mb-6 bg-[#FEF8F0] border border-[#C93B2B] rounded-sm p-5" data-testid="selected-banner">
              <p className="text-sm font-medium text-[#1A1A1A] mb-3">
                你已選擇 {myOrders.length} / {maxPicks} 位命理師{isFull ? '（已滿）' : `，仲可以再揀 ${maxPicks - myOrders.length} 位`}
              </p>
              <div className="flex flex-wrap gap-3">
                {myOrders.map((o) => (
                  <Link key={o.order_id} to={`/chat/${o.order_id}`}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm border-[#C93B2B] text-[#C93B2B] hover:bg-[#F5F0E6]"
                      data-testid={`go-chat-${o.order_id}`}
                    >
                      <MessageCircle className="mr-2 w-4 h-4" strokeWidth={1.5} />
                      {o.other_party_name} · 諮詢室
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {(() => {
            const qs = request.verification_questions || [];
            if (!qs.length) return null;
            const myTruths = request.my_truths || {};
            const unfilled = qs.filter((q) => !(String(q.order) in myTruths));
            return (
              <div className="mb-6 bg-white border border-[#C93B2B] rounded-sm p-5" data-testid="truth-backfill">
                <p className="text-sm font-medium text-[#1A1A1A] mb-1">你的真相（評分依據）</p>
                <p className="text-xs text-[#5C5C5C] mb-3">
                  真相要<b>一次過填曬所有問題</b>（私下，<b>唔會畀命理師睇</b>），填咗就鎖定，用嚟評命理師準確度。
                </p>
                <div className="space-y-3">
                  {qs.map((q) => {
                    const t = myTruths[String(q.order)];
                    return (
                      <div key={q.order}>
                        <p className="text-sm text-[#1A1A1A] mb-1">問題 {q.order}：{q.question_text}</p>
                        {t ? (
                          <p className="text-sm bg-[#F5F0E6] rounded-sm p-2 text-[#1A1A1A]" data-testid={`my-truth-${q.order}`}>🔒 你填咗：{t}</p>
                        ) : (
                          <textarea
                            value={truthInputs[q.order] || ''}
                            onChange={(e) => setTruthInputs((p) => ({ ...p, [q.order]: e.target.value }))}
                            placeholder="你知道嘅真實答案（要一次過填曬所有問題）"
                            rows={2}
                            maxLength={50}
                            className="w-full rounded-sm border border-[#E5E0D8] p-2 text-sm"
                            data-testid={`truth-input-${q.order}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {unfilled.length > 0 && (
                  <Button onClick={submitTruths} disabled={savingTruths}
                    className="mt-3 bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm" data-testid="submit-truths-btn">
                    {savingTruths ? '評分中…' : '提交真相並評分'}
                  </Button>
                )}
              </div>
            );
          })()}

          {/* Filters */}
          <div className="flex flex-wrap gap-4 justify-between items-center mb-6 bg-white border border-[#E5E0D8] rounded-sm p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-3">
                <Filter className="w-5 h-5 text-[#C93B2B]" strokeWidth={1.5} />
                <span className="text-sm font-medium text-[#1A1A1A]">價格篩選：</span>
                <Select value={priceFilter} onValueChange={setPriceFilter}>
                  <SelectTrigger className="w-[160px] rounded-sm" data-testid="price-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      // Bucket thresholds live in TWD; show them in the viewer's display currency.
                      const dc = toCode(displayCurrency);
                      const approx = dc === 'TWD' ? '' : '≈ ';
                      const th = (n) => `${symbolOf(dc)} ${Math.round(convert(n, 'TWD', dc, rates)).toLocaleString()}`;
                      return (
                        <>
                          <SelectItem value="all">全部價格</SelectItem>
                          <SelectItem value="low">{approx}{th(2000)} 以下</SelectItem>
                          <SelectItem value="medium">{approx}{th(2000)} - {th(3500)}</SelectItem>
                          <SelectItem value="high">{approx}{th(3500)} 以上</SelectItem>
                        </>
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <ArrowUpDown className="w-5 h-5 text-[#C93B2B]" strokeWidth={1.5} />
                <span className="text-sm font-medium text-[#1A1A1A]">排序：</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px] rounded-sm" data-testid="sort-by">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">預設順序</SelectItem>
                    <SelectItem value="score-desc">本單準度：高至低</SelectItem>
                    <SelectItem value="price-asc">價格：低至高</SelectItem>
                    <SelectItem value="price-desc">價格：高至低</SelectItem>
                    <SelectItem value="time-asc">諮詢時間：短至長</SelectItem>
                    <SelectItem value="time-desc">諮詢時間：長至短</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-[#1A1A1A]">顯示貨幣：</span>
                <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
                  <SelectTrigger className="w-[140px] rounded-sm" data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.symbol} {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-sm text-[#5C5C5C]">
              顯示 {filteredAnswers.length} 位命理師 · 匯率約值
            </div>
          </div>

          {filteredAnswers.length === 0 ? (
            <div className="bg-white border border-[#E5E0D8] rounded-sm p-12 text-center">
              <p className="text-sm text-[#5C5C5C]">此價格範圍內沒有命理師回答</p>
              <Button
                variant="outline"
                onClick={() => setPriceFilter('all')}
                className="mt-4 rounded-sm border-[#E5E0D8]"
                data-testid="reset-filter-btn"
              >
                查看全部
              </Button>
            </div>
          ) : (
            <>
              {/* ===== Mobile: stacked cards (one astrologer each) ===== */}
              <div className="md:hidden space-y-6">
                {filteredAnswers.map((answer) => (
                  <div
                    key={`m-${answer.answer_id}`}
                    className="border border-[#E5E0D8] rounded-sm bg-white"
                    data-testid={`answer-column-${answer.answer_id}`}
                  >
                    <div className="border-b border-[#E5E0D8] p-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-serif text-[#1A1A1A]">{answer.astrologer_name}</h3>
                        <StarButton answer={answer} />
                      </div>
                      <Badges profile={answer.astrologer_profile} />
                      <AccuracyMeter answer={answer} className="mb-2" />
                      <Quotes answer={answer} className="mb-3" />
                      {answer.astrologer_profile?.languages?.length > 0 && (
                        <p className="text-xs text-[#5C5C5C] mb-3">語言：{answer.astrologer_profile.languages.join('、')}</p>
                      )}
                      <SelectButton answer={answer} className="w-full" />
                      <div className="mt-2 flex justify-end">
                        <ReportButton targetType="user" targetId={answer.astrologer_id} label="舉報此命理師" testId={`report-astrologer-${answer.astrologer_id}`} />
                      </div>
                    </div>
                    <div className="p-5 space-y-5">
                      {request.verification_questions.map((q) => {
                        const a = answer.answers.find(x => x.question_order === q.order);
                        const g = questionGrade((answer.question_scores || []).find((x) => x.order === q.order));
                        return (
                          <div key={`m-${answer.answer_id}-q${q.order}`}>
                            <div className="bg-[#F5F0E6] rounded-sm p-3 mb-2">
                              <p className="text-xs text-[#C93B2B] font-bold">問題 {q.order}</p>
                              <p className="text-xs text-[#5C5C5C] mt-1">{q.question_text}</p>
                            </div>
                            <p className="text-sm text-[#1A1A1A] leading-relaxed">{a ? a.answer_text : '—'}</p>
                            {g && <span className={`inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded-sm border ${g.cls}`}>{g.label}</span>}
                          </div>
                        );
                      })}
                      <div className="border-t border-[#E5E0D8] pt-4">
                        <p className="text-xs text-[#5C5C5C] font-bold mb-2">補充觀察</p>
                        <p className="text-sm text-[#1A1A1A] leading-relaxed">{answer.observation || '—'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ===== Desktop: aligned matrix (rows = questions, cols = astrologers) ===== */}
              <div className="hidden md:block overflow-x-auto pb-4">
                <div
                  className="grid gap-px bg-[#E5E0D8] border border-[#E5E0D8] rounded-sm min-w-full"
                  style={{ gridTemplateColumns: `170px repeat(${filteredAnswers.length}, minmax(240px, 1fr))` }}
                >
                  {/* Header row */}
                  <div className="bg-[#FDFBF7] sticky left-0 top-0 z-30" />
                  {filteredAnswers.map((answer) => (
                    <div
                      key={`h-${answer.answer_id}`}
                      className="bg-white sticky top-0 z-20 p-4"
                      data-testid={`answer-column-${answer.answer_id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-base font-serif text-[#1A1A1A]">{answer.astrologer_name}</h3>
                        <StarButton answer={answer} />
                      </div>
                      <Badges profile={answer.astrologer_profile} />
                      <AccuracyMeter answer={answer} className="mb-2" />
                      <Quotes answer={answer} className="mb-3" />
                      {answer.astrologer_profile?.languages?.length > 0 && (
                        <p className="text-xs text-[#5C5C5C] mb-3">語言：{answer.astrologer_profile.languages.join('、')}</p>
                      )}
                      <SelectButton answer={answer} className="w-full" />
                      <div className="mt-2 flex justify-end">
                        <ReportButton targetType="user" targetId={answer.astrologer_id} label="舉報" testId={`report-astrologer-${answer.astrologer_id}`} />
                      </div>
                    </div>
                  ))}

                  {/* One row per verification question */}
                  {request.verification_questions.map((q) => (
                    <Fragment key={`row-${q.order}`}>
                      <div className="bg-[#F5F0E6] sticky left-0 z-10 p-4">
                        <p className="text-xs text-[#C93B2B] font-bold mb-1">問題 {q.order}</p>
                        <p className="text-xs text-[#5C5C5C] leading-relaxed">{q.question_text}</p>
                      </div>
                      {filteredAnswers.map((answer) => {
                        const a = answer.answers.find(x => x.question_order === q.order);
                        const g = questionGrade((answer.question_scores || []).find((x) => x.order === q.order));
                        return (
                          <div key={`c-${answer.answer_id}-${q.order}`} className="bg-white p-4">
                            {g && <span className={`inline-block mb-1.5 text-[10px] px-1.5 py-0.5 rounded-sm border ${g.cls}`}>{g.label}</span>}
                            <p className="text-sm text-[#1A1A1A] leading-relaxed">{a ? a.answer_text : '—'}</p>
                          </div>
                        );
                      })}
                    </Fragment>
                  ))}

                  {/* Observation row */}
                  <div className="bg-[#F5F0E6] sticky left-0 z-10 p-4">
                    <p className="text-xs text-[#5C5C5C] font-bold">補充觀察</p>
                  </div>
                  {filteredAnswers.map((answer) => (
                    <div key={`obs-${answer.answer_id}`} className="bg-white p-4">
                      <p className="text-sm text-[#1A1A1A] leading-relaxed">{answer.observation || '—'}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 bg-[#FEF8F0] border border-[#C93B2B] rounded-sm p-6 max-w-3xl mx-auto">
                <p className="text-sm text-[#1A1A1A] font-medium">
                  💡 提示：比較時請注意回答的準確度、洞察深度和表達方式。{free ? '選定後即可開通諮詢室，與命理師進行更深入的諮詢。' : '選定後付款，就能與命理師進行更深入的諮詢。'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompareAnswers;
